import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Tag } from '@maiia/design-system';
import { DEFAULT_TIMEZONE } from '@/src/constants';
import { useTranslation } from '@/src/i18n';

type Props = {
  card: CardHcd;
};

type RawScheduler = {
  isActive: boolean;
  schedules: Array<{
    startTime: string;
    endTime: string;
  }>;
};

type Scheduler = {
  isActive: boolean;
  schedules: Array<{
    startTime: Dayjs;
    endTime: Dayjs;
  }>;
};

type DayOfWeek =
  | 'SUNDAY'
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY';

const DAY_OF_WEEK_MAPS: DayOfWeek[] = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

const hhmmtodayjs = (hhmm: string, tz: string) => {
  const [hh, mm] = hhmm.split(':');
  return dayjs()
    .tz(tz)
    .set({
      hour: parseInt(hh, 10),
      minute: parseInt(mm, 10),
      second: 0,
      millisecond: 0,
    });
};

const dayjsifySchedules = (
  openingSchedules: Record<DayOfWeek, RawScheduler>,
  tz: string,
): Record<DayOfWeek, Scheduler> => {
  const res = {} as Record<DayOfWeek, Scheduler>;
  for (const [k, v] of Object.entries(openingSchedules)) {
    res[k] = {
      isActive: v.isActive,
      schedules: v.schedules.map(slot => ({
        startTime: hhmmtodayjs(slot.startTime, tz),
        endTime: hhmmtodayjs(slot.endTime, tz),
      })),
    };
  }
  return res;
};

const getOpeningStatus = (
  openingSchedules: Record<DayOfWeek, Scheduler>,
  tz: string,
): [boolean, number] => {
  const now = dayjs().tz(tz);
  const tomorrow = now
    .clone()
    .add(1, 'day')
    .set({ hours: 0, minute: 0, second: 0, millisecond: 0 });
  let nextTime = +tomorrow - +now;
  const todayScheduler = openingSchedules?.[DAY_OF_WEEK_MAPS[now.get('day')]];
  if (!todayScheduler || !todayScheduler.isActive) {
    return [false, nextTime];
  }
  for (const slot of todayScheduler.schedules) {
    if (+now >= +slot.startTime && +now <= +slot.endTime)
      return [true, +slot.endTime.clone().add(1, 'second')];
  }

  // Find the next time change status in today
  for (const slot of todayScheduler.schedules) {
    if (+now < +slot.startTime) {
      const diff = +slot.startTime.clone().add(1, 'second') - +now;
      if (diff < nextTime) nextTime = diff;
    }
  }

  return [false, nextTime];
};

const OpeningStatus = ({ card }: Props) => {
  const { t } = useTranslation();
  const openingSchedules = card?.publicInformation?.officeInformation
    ?.openingSchedules as Record<DayOfWeek, RawScheduler> | undefined;
  const tz = card?.settings?.timeZone || DEFAULT_TIMEZONE;
  const dayjsSchedules = useMemo(
    () => openingSchedules && dayjsifySchedules(openingSchedules, tz),
    [openingSchedules, tz],
  );
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    if (!dayjsSchedules) return;
    let timeoutId: NodeJS.Timeout | undefined;
    const updateStatus = () => {
      const [status, nextTimeChange] = getOpeningStatus(dayjsSchedules, tz);
      setIsOpening(status);
      timeoutId = setTimeout(updateStatus, nextTimeChange);
    };
    updateStatus();
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [dayjsSchedules, tz]);

  if (!openingSchedules) return null;

  return (
    // This link will add an anchor to scroll the view into the schedules
    // section. See Schedules.tsx to view the code responsible for the scroll
    <Tag size="small" mode="light">
      <a href="#schedules">
        {isOpening
          ? t('open', { context: 'female' })
          : t('closed', { context: 'female' })}
      </a>
    </Tag>
  );
};

export default OpeningStatus;
